import { FC, ReactNode, useMemo } from "react"
import classNames from "classnames"
import { Icon, IconColor, IconProps } from "@/components/shared/Icon"
import "./index.scss"

export enum TabVariant {
   DEFAULT = "default",
   PILL = "pill",
}

type Tab = {
   id: string
   tabIcon?: IconProps["name"]
   label: string
   content?: ReactNode
   route?: string
   disabled?: boolean
   ["data-testid"]?: string
}

interface TabsProps {
   variant?: TabVariant
   activeTabId: string
   tabs: Tab[]
   className?: string
   onTabChange?: (id: string) => void
   buttonsClassName?: string
}

const Tabs: FC<TabsProps> = ({
   variant = TabVariant.DEFAULT,
   activeTabId,
   tabs,
   className,
   onTabChange,
   buttonsClassName,
}) => {
   const handleTabChange = (tab: Tab) => {
      if (onTabChange) {
         onTabChange(tab.id)
      }
   }

   const content = useMemo(() => {
      const activeTab = tabs.find((tab) => tab.id === activeTabId)
      if (activeTab) {
         return activeTab.content
      }
   }, [activeTabId, tabs])

   return (
      <div className={classNames("tabs__container", className)}>
         <div
            className={classNames("tabs__buttons-wrapper", buttonsClassName, {
               "tabs__buttons-wrapper--pill": variant === TabVariant.PILL,
            })}
         >
            {tabs.map((tab) => (
               <button
                  key={tab.id}
                  className={classNames("tabs__button", {
                     "tabs__button--active": activeTabId === tab.id,
                     "tabs__button--pill": variant === TabVariant.PILL,
                  })}
                  onClick={(e) => {
                     e.preventDefault()
                     handleTabChange(tab)
                  }}
                  disabled={tab.disabled}
                  {...(tab["data-testid"] && {
                     "data-testid": tab["data-testid"],
                  })}
               >
                  {tab.label}
                  {tab.tabIcon && (
                     <Icon
                        color={
                           activeTabId === tab.id
                              ? IconColor.WHITE
                              : IconColor.GUNMETAL
                        }
                        size="10"
                        name={tab.tabIcon}
                        className="tabs__button-icon"
                     />
                  )}
               </button>
            ))}
         </div>
         {content && <div className="tabs__tab">{content}</div>}
      </div>
   )
}

export default Tabs
